import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { SketchPicker } from 'react-color';
import { Body1, Body5, Body7, Container } from '@ui';
import { FlatButton, TransparentFlatButton, CollapseButton, CloseButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { colorPalette } from '@theme';
import { colorThemeActions } from '@constants/actionTypes';
import { colorThemeNameDisplayName } from '@utils/themes';
import { getContrastTheme } from '@utils/usefulFuncs';
import { breakpoints } from '@constants/breakpoints';
import { FAVORITES_MAX } from '@utils/themes';
import { SHADOW_CARD, SHADOW_POPOVER } from './styles';

const TOKEN_GROUPS = [
  {
    key: 'surfaces',
    label: 'Surfaces',
    description: 'App and panel background colors',
    keys: ['appBackground', 'componentNoOpacity'],
  },
  {
    key: 'content',
    label: 'Content',
    description: 'Primary text color',
    keys: ['text'],
  },
  {
    key: 'brand',
    label: 'Brand',
    description: 'Primary interactive and button colors',
    keys: ['primaryCtaButton', 'buttonText'],
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    description: 'Value change and status indicators',
    keys: ['positive', 'neutral', 'negative'],
  },
  {
    key: 'analyst',
    label: 'Analyst',
    description: 'Pro and con call-out colors',
    keys: ['proColor', 'conColor'],
  },
];

const EditorCard = styled.div`
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border-radius: 8px;
  box-shadow: ${SHADOW_CARD};
  overflow: hidden;
`;

const EditorCardInner = styled.div`
  padding: 20px;
`;

const ThemeNameInput = styled.input`
  font-size: 14px;
  padding: 9px 12px;
  width: 100%;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  background: ${({ theme }) => theme.themeColors.appBackground};
  color: ${({ theme }) => theme.themeColors.text};
  box-sizing: border-box;
  outline: none;
  transition: border-color 120ms ease;

  &:focus {
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }

  &::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }
`;

const GroupSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  margin-top: 4px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

// CollapseButton has padding-bottom: 8px baked in — this wrapper cancels it so
// the chevron centers correctly in any align-items: center flex row.
const CollapseAlignFix = styled.div`
  margin-bottom: -8px;
`;

const TokenRowWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const ColorSwatch = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: ${({ $color }) => $color || '#ccc'};
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
  padding: 0;

  &:hover {
    transform: scale(1.06);
    box-shadow: ${SHADOW_POPOVER};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 2px;
  }
`;

const LabelGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

const HexInput = styled.input`
  font-size: 12px;
  padding: 6px 8px;
  width: 96px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: ${({ $color }) => $color || 'transparent'};
  color: ${({ $color }) =>
    getContrastTheme($color, {
      dark: colorPalette.primary.oak,
      light: colorPalette.primary.charcoal,
    }) || colorPalette.primary.charcoal};
  outline: none;
  transition: border-color 120ms ease;

  &:focus {
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }
`;

const PickerPopover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  /* SketchPicker renders on a white background; keep white so the picker stays
     readable, but add a border so the popover has a defined edge on light themes */
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: ${SHADOW_POPOVER};
  overflow: hidden;

  @media (max-width: ${() => breakpoints.mobile}) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const PickerInner = styled.div`
  .sketch-picker {
    width: 260px !important;
    box-shadow: none !important;
    border-radius: 0 !important;

    * {
      color: ${() => colorPalette.primary.charcoal} !important;
    }

    .flexbox-fix:nth-child(2) {
      div + :last-of-type {
        display: none !important;
      }
    }
  }
`;

const PickerTopBar = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseWrap = styled.div`
  cursor: pointer;

  svg {
    height: 11px;
    width: 11px;
    opacity: 0.45;
  }

  path {
    fill: #000 !important;
  }
`;

const AddFavWrap = styled.div`
  button {
    padding: 3px 8px;
    font-size: 11px;
  }
`;

const FavStrip = styled.div`
  padding: 8px 12px 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const FavSwatch = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: transform 120ms ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.12);
  }

  &:hover .fav-remove {
    display: flex;
  }
`;

const FavRemoveX = styled.div`
  display: none;
  position: absolute;
  top: -4px;
  right: -4px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${({ theme }) => theme.themeColors.text};
  align-items: center;
  justify-content: center;
  font-size: 7px;
  color: ${({ theme }) => theme.themeColors.componentNoOpacity};
  line-height: 1;
  z-index: 5;
  cursor: pointer;
`;

const FooterDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  margin-top: 20px;
  padding-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DerivedNote = styled.div`
  margin-top: 16px;
`;

const ColorTokenRow = ({ name, keyName, value, isEditing, setIsEditing, favorites, addFavorite, removeFavorite }) => {
  const dispatch = useDispatch();
  const pickerRef = React.useRef(null);
  const [previewColor, setPreviewColor] = React.useState(null);

  React.useEffect(() => {
    if (value) setPreviewColor(value);
  }, [value]);

  React.useEffect(() => {
    if (!isEditing) return;

    const handleMouseDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsEditing(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsEditing(null);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, setIsEditing]);

  const handleChange = ({ hex }) => setPreviewColor(hex);

  const handleChangeComplete = ({ hex }) => {
    setPreviewColor(null);
    dispatch({
      type: colorThemeActions.setCustomColorThemeColorDefinition,
      payload: { [keyName]: hex },
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: colorThemeActions.setCustomColorThemeColorDefinition,
      payload: { [keyName]: e.target.value },
    });
  };

  const handleFavClick = (hex) => {
    dispatch({
      type: colorThemeActions.setCustomColorThemeColorDefinition,
      payload: { [keyName]: hex },
    });
    setIsEditing(null);
  };

  const currentColor = previewColor || value;
  const presetColors = favorites.map((hex) => ({ color: hex, title: hex }));
  const favLimitReached = favorites.length >= FAVORITES_MAX;

  return (
    <TokenRowWrapper>
      <ColorSwatch
        $color={currentColor}
        onClick={() => setIsEditing(keyName)}
        aria-label={`Edit color for ${name}: ${value}`}
      />
      <LabelGroup>
        <Body5>{name}</Body5>
      </LabelGroup>
      <HexInput
        $color={value}
        value={value}
        onChange={handleInputChange}
        onFocus={() => setIsEditing(keyName)}
        maxLength={7}
        aria-label={`Hex value for ${name}`}
      />
      {isEditing && (
        <PickerPopover ref={pickerRef}>
          <PickerTopBar>
            <Body5 color={colorPalette.primary.charcoal}>Color Selector</Body5>
            <Container row verticallyCenter gap={8}>
              <AddFavWrap>
                {favLimitReached ? (
                  <SkeletonButton disabled>★ Favorite</SkeletonButton>
                ) : (
                  <SkeletonButton onClick={() => addFavorite(currentColor)}>★ Favorite</SkeletonButton>
                )}
              </AddFavWrap>
              <CloseWrap>
                <CloseButton onClick={() => setIsEditing(null)} />
              </CloseWrap>
            </Container>
          </PickerTopBar>
          {favLimitReached && (
            <Container horizontal={10} bottom={4}>
              <Body7 color={colorPalette.primary.charcoal}>Favorites limit reached (24)</Body7>
            </Container>
          )}
          {favorites.length > 0 && (
            <FavStrip>
              {favorites.map((hex) => (
                <FavSwatch
                  key={hex}
                  $color={hex}
                  title={hex}
                  onClick={() => handleFavClick(hex)}
                  aria-label={`Apply favorite color ${hex}`}
                >
                  <FavRemoveX
                    className="fav-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(hex);
                    }}
                  >
                    ×
                  </FavRemoveX>
                </FavSwatch>
              ))}
            </FavStrip>
          )}
          <PickerInner>
            <SketchPicker
              color={currentColor}
              onChange={handleChange}
              onChangeComplete={handleChangeComplete}
              presetColors={presetColors}
            />
          </PickerInner>
        </PickerPopover>
      )}
    </TokenRowWrapper>
  );
};

const ColorGroupSection = ({ group, customTheme, isEditing, setIsEditing, favorites, addFavorite, removeFavorite }) => {
  const [collapsed, setCollapsed] = React.useState(false);

  return (
    <GroupSection>
      <GroupHeader onClick={() => setCollapsed((c) => !c)}>
        <Container>
          <Body1 bold>{group.label}</Body1>
          <Container top={2}>
            <Body7 isLowContrast>{group.description}</Body7>
          </Container>
        </Container>
        <CollapseAlignFix>
          <CollapseButton isCollapsed={collapsed} setCollapsed={setCollapsed} />
        </CollapseAlignFix>
      </GroupHeader>
      {!collapsed && (
        <Container bottom={4}>
          {group.keys.map((k) => (
            <ColorTokenRow
              key={k}
              name={colorThemeNameDisplayName[k]}
              keyName={k}
              value={customTheme[k]}
              isEditing={isEditing === k}
              setIsEditing={setIsEditing}
              favorites={favorites}
              addFavorite={addFavorite}
              removeFavorite={removeFavorite}
            />
          ))}
        </Container>
      )}
    </GroupSection>
  );
};

export const ColorEditorPanel = ({
  customTheme,
  colorThemeName,
  onNameChange,
  onSave,
  onExport,
  onReset,
  favorites,
  addFavorite,
  removeFavorite,
}) => {
  const [isEditing, setIsEditing] = React.useState(null);
  const canSave = colorThemeName.trim().length > 0;

  return (
    <EditorCard>
      <EditorCardInner>
        <Container bottom={16}>
          <Container bottom={6}>
            <Body5 bold>Theme name</Body5>
          </Container>
          <ThemeNameInput
            value={colorThemeName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. My Dark Theme"
          />
        </Container>

        {TOKEN_GROUPS.map((group) => (
          <ColorGroupSection
            key={group.key}
            group={group}
            customTheme={customTheme}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            favorites={favorites}
            addFavorite={addFavorite}
            removeFavorite={removeFavorite}
          />
        ))}

        <DerivedNote>
          <Body7 isLowContrast>14 additional colors are computed automatically from your selections.</Body7>
        </DerivedNote>

        <FooterDivider>
          <FlatButton onClick={onSave} disabled={!canSave}>
            Save theme
          </FlatButton>
          <SkeletonButton onClick={onExport}>Export draft</SkeletonButton>
          <Container alignRight flex={1}>
            <TransparentFlatButton onClick={onReset}>Reset to default</TransparentFlatButton>
          </Container>
        </FooterDivider>
        {!canSave && (
          <Container top={8}>
            <Body7 isLowContrast>Add a name to save</Body7>
          </Container>
        )}
      </EditorCardInner>
    </EditorCard>
  );
};
